import {useState,useEffect}  from "react";
import {   
    makeStyles,
    mergeClasses,
    Dialog,
    DialogSurface,
    DialogTitle,
    DialogBody,
    DialogActions,
    DialogContent,
    DialogTrigger,
    Button,
    TableBody,
    TableCell,
    TableRow,
    Table,
    TableHeader,
    TableHeaderCell,
    TableCellLayout,
    useFocusableGroup,
    tokens,
    Subtitle2,
    Textarea,
    Spinner,
    useToastController
    } from "@fluentui/react-components";
import {EditRegular, DeleteRegular} from "@fluentui/react-icons";
import TimePicker from './TimePicker';
import {IsCompleteTime, DifferenceInMinutes} from '../../services/TimeFunctions';
import {format} from "date-fns";
import {SetTimeEntryDetails} from '../../services/apiService';
import {useGlobalStatus} from '../../services/GlobalStatusProvider';
import TooltipWatcherWidget from "../TooltipWatcherWidget";
import { showErrorToast, showSuccessToast, TOASTER_ID } from "../../services/toastUtils";

const useStylesEditor = makeStyles({
    dialog: {
        width: '90vw',
        maxWidth: '800px',
        padding: '1rem',
        overflowX: 'auto',
    },
    
    infobar :{
        color: tokens.colorBrandForeground1,
        backgroundColor: tokens.colorBrandBackground2,
        padding: '3px'
    }
});

export default function EntryCellEditor(props) { 
    const{startLoad, endLoad, isDesktop, useStyles, loading}= useGlobalStatus();
    const globalStyles = useStyles();
    const [entry, setEntry]=useState({
            start:'',
            end: '',
            break:'',
            comments:'',
            totalMinutes: 0,
            id:0,
            startEndValidationState:'none',
            breakValidationState: 'none',
            isValid:false
    });
    const [open, setOpen] = useState(false);
    const [entryToDelete, setEntryToDelete] = useState(null);
    const [disableConfirmForAPI, setDisableConfirmForAPI] = useState(false);
    const { dispatchToast } = useToastController(TOASTER_ID);

    // Reset input fields when the dialog closes
    useEffect(() => {
        if (!props.dialogOpen) {
            clear(); 
        }
    }, [props.dialogOpen]);
    
    const styles = useStylesEditor();

    const handleRowEdit = (id)=> {
        setEntry(props.entryDay.entries.find(q=> q.id === id));
    }

    const handleRowDelete = async (id)=> {
        startLoad();
        var entryToDelete = props.entryDay.entries.find(q=> q.id === id);
        // Delete from A23 DB
        var timeEntryDetail = {
            start:entryToDelete.start,
            end: entryToDelete.end,
            break:entryToDelete.break,
            comments:entryToDelete.comments,
            timeEntryDetailId:entryToDelete.id};

        var date = format(props.entryDay.date, "yyyy-MM-dd HH:mm:ss");
        let deletedBool = false;
        try {
            deletedBool = await SetTimeEntryDetails(props.entryData.taskId, date, timeEntryDetail, true);
            showSuccessToast(dispatchToast, "Successfully deleted entry");
        } catch {
            showErrorToast(dispatchToast, "Failed to delete entry");
        }

        if (deletedBool) {
            var day = {
                ...props.entryDay,
                entries: props.entryDay.entries.filter(item => item.id !== id)
            };
              props.handleUpdateWeekView(props.entryData, day); 
        } else {
            showErrorToast(dispatchToast, "Failed to delete entry");
        }
        props.suggestedStartDict[format(new Date(props.entryDay.date), 'dd/MM/yyyy')] = entry.end;
        props.setSuggestedStartDict(props.suggestedStartDict);
        endLoad();
        props.handleDialogClose();
    }

    const handleDialogCancel = () =>{
        props.handleDialogClose();
    };

    const handleDialogAdd = async () =>{
        // Validate break format before submission
        const isValidBreak = entry.break === "" || /^([01]\d|2[0-3]):([0-5]\d)$/.test(entry.break);
        if (!isValidBreak) {
            setEntry(prev => ({
                ...prev,
                breakValidationState: "error"
            }));
            return;
        }

        startLoad();
        setDisableConfirmForAPI(true);
        var timeEntryDetail = {
            start:entry.start,
            end: entry.end,
            break:entry.break,
            comments:entry.comments,
            timeEntryDetailId:entry.id};
        
        var date = format(props.entryDay.date, "yyyy-MM-dd HH:mm:ss");
        let newid = null;
        try {
            newid = await SetTimeEntryDetails(props.entryData.taskId, date, timeEntryDetail, false);
            showSuccessToast(dispatchToast, "Successfully updated entry");
        } catch {
            showErrorToast(dispatchToast, "Failed to update entry");
        }
        
        var day = props.entryDay;

        if(entry.id===0){
            //Add a new record
            if(!isNaN(newid)){
                entry.id= newid;
                day.entries.push(entry);
            }
        } else {
            const entryIndex = day.entries.findIndex(existingEntry => existingEntry.id === entry.id);
            if (entryIndex !== -1) {
                day.entries[entryIndex] = { ...day.entries[entryIndex], ...entry };
            }
        }

        endLoad();
        setDisableConfirmForAPI(false);
        props.handleDialogClose();
        props.handleUpdateWeekView(props.entryData, day);
        props.suggestedStartDict[format(new Date(props.entryDay.date), 'dd/MM/yyyy')] = entry.end;
        props.setSuggestedStartDict(props.suggestedStartDict);
        clear();
    };
    

const handleSetEntry = (source, value) => {
    var instance = { ...entry };

    switch (source) {
        case "Start":
            instance.start = value;
            break;
        case "End":
            instance.end = value;
            break;
        case "Break":
            instance.break = value;
            instance.breakValidationState = "none";
            break;
        default:
            if (value !== undefined) {
                instance.comments = value.value;
            }
            break;
    }

    // Perform the validation
    if (IsCompleteTime(instance.start) && IsCompleteTime(instance.end)) {
        var difference = DifferenceInMinutes(instance.end, instance.start);
        
        if (difference < 1) {
            instance.startEndValidationState = "error";
            instance.totalMinutes = 0;
            instance.isValid = false;
        } else {
            instance.startEndValidationState = "none";
            instance.totalMinutes = difference;
            instance.isValid = true;

            if (IsCompleteTime(instance.break)) {
                var total = DifferenceInMinutes(difference, instance.break);
                if (total < 1) {
                    instance.breakValidationState = "error";
                    instance.totalMinutes = 0;
                    instance.isValid = false;
                } else {
                    instance.breakValidationState = "none";
                    instance.totalMinutes = total;
                    instance.isValid = true;
                }
            }
        }

        // Overlap Validation using all entries across same day
        const allEntriesSameDay = [];
        props.timesheetData.forEach(client => {
            client.projects.forEach(project => {
                project.budgets.forEach(budget => {
                    budget.timecodes.forEach(timecode => {
                        timecode.entryDays.forEach(entryDay => {
                            if (new Date(entryDay.date).toISOString().split("T")[0] === new Date(props.entryDay.date).toISOString().split("T")[0]) { 
                                allEntriesSameDay.push(...entryDay.entries);
                            }
                        });
                    });
                });
            });
        });
        
        let hasOverlap = allEntriesSameDay.some(existingEntry => {
            if (existingEntry.id !== instance.id) {
                return (
                    (instance.start >= existingEntry.start && instance.start < existingEntry.end) || 
                    (instance.end > existingEntry.start && instance.end <= existingEntry.end) || 
                    (instance.start <= existingEntry.start && instance.end >= existingEntry.end)
                );
            }
            return false;
        });

        if (hasOverlap) {
            instance.overlapValidationState = "error";
            instance.isValid = false;
        } else {
            instance.overlapValidationState = "none";
        }
    }
    setEntry(instance);
};

    const clear = ()=> {
        setEntry({
            start:'',
            end: '',
            break:'',
            comments:'',
            totalMinutes: 0,
            id:0,
            startEndValidationState:'none',
            breakValidationState: 'none',
            overlapValidationState: 'none',
            isValid:false
        });
    }

    const columns = [
        { columnKey: "start", label: "Start" },
        { columnKey: "end", label: "End" },
        { columnKey: "break", label: "Break" },
        { columnKey: "comments", label: "Comments" },
        { columnKey: "total", label: "Total" },
        { columnKey: "actions", label: "Actions" },
      ];

    const mobileColumns = [
        { columnKey: "start", label: "Start" },
        { columnKey: "end", label: "End" },
        { columnKey: "break", label: "Break" },
        { columnKey: "total", label: "Total" },
        { columnKey: "actions", label: "Delete" },
      ];

    const focusableGroupAttr = useFocusableGroup({
        tabBehavior: "limited-trap-focus",
    });

    return (
        <Dialog open={props.dialogOpen} onOpenChange={(e, { open }) => handleDialogCancel()}>                
            <DialogSurface className={styles.dialog}>
                <DialogBody >
                
                <DialogTitle>
                    {props.dialogOpen && (
                        <div style={{ display: "flex", flexDirection: "column" }}>
                            {props.entryDay.status === "empty" ? "Add new record" : "Modify record"} -  {format(props.entryDay.date, "PPPP")}
                            <span style={{ fontSize: "14px", color: "#888" }}>
                                {props.entryData.projectName} - {props.entryData.taskName}
                            </span>
                        </div>
                    )}
                </DialogTitle>
                <DialogContent >
                    {
                        (props.dialogOpen) &&                    
                        <div>
                            <Table className="editor-approval-table">
                                <TableHeader>
                                    <TableRow>
                                        {(isDesktop ? columns : mobileColumns).map((column) => (
                                            <TableHeaderCell key={column.columnKey}>
                                                {column.label}
                                            </TableHeaderCell>
                                            ))}
                                    </TableRow>
                                </TableHeader>                           
                                <TableBody>
                            {
                                props.entryDay.entries.map((entry)=>(
                                    <TableRow key={entry.id} onClick={()=> {handleRowEdit(entry.id)}}>
                                        <TableCell>
                                            <TableCellLayout>
                                                {entry.start}
                                            </TableCellLayout>
                                        </TableCell>
                                        <TableCell>
                                            <TableCellLayout>
                                                {entry.end}
                                            </TableCellLayout>
                                        </TableCell>
                                        <TableCell>
                                            <TableCellLayout>
                                                {entry.break}
                                            </TableCellLayout>
                                        </TableCell>
                                        {isDesktop && <TableCell>
                                            <TableCellLayout>
                                                {entry.comments}
                                            </TableCellLayout>
                                        </TableCell>}
                                        
                                        <TableCell>
                                            <TableCellLayout>
                                                {(Math.round(entry.totalMinutes / 60 * 100) / 100).toFixed(2)}hr
                                            </TableCellLayout>
                                        </TableCell>
                                        <TableCell role="gridcell" {...focusableGroupAttr}>
                                            <TableCellLayout>
                                                {isDesktop && <Button icon={<EditRegular/>} aria-label="Edit" onClick={()=> {handleRowEdit(entry.id)}}/>}
                                                <Dialog open={open} onOpenChange={(e, { open }) => setOpen(open)}>
                                                <DialogTrigger disableButtonEnhancement>
                                                    <Button icon={<DeleteRegular />} aria-label="Delete" onClick={() => {setOpen(true); setEntryToDelete(entry.id);}} />
                                                </DialogTrigger>
                                                <DialogSurface>
                                                    <DialogBody>
                                                        <DialogTitle>Confirmation</DialogTitle>
                                                        <DialogContent>Are you sure you want to delete the entry?</DialogContent>
                                                        <DialogActions>
                                                            <Button className={globalStyles.buttonPrimary} onClick={() => { handleRowDelete(entryToDelete); setOpen(false); }}>Yes</Button>
                                                            <Button appearance="secondary" onClick={() => setOpen(false)}>No</Button>
                                                        </DialogActions>
                                                    </DialogBody>
                                                </DialogSurface>
                                            </Dialog>
                                            </TableCellLayout>
                                        </TableCell>
                                    </TableRow>
                                ))
                            }
                                </TableBody>                         
                            </Table>
                            {
                                (props.entryDay.entries.length===0) &&
                                    <div className={mergeClasses('infobar-layout', styles.infobar)}><Subtitle2>There are no entries</Subtitle2></div>                                
                            }  

                            <div className="editor-form">
                                <div className="editor-format-tip">Enter Start, End, and Break times in 24-hour format (e.g. 08:30)</div>
                                <div className="editor-error-message">
                                    {(entry.overlapValidationState === "error" || entry.startEndValidationState === "error" || entry.breakValidationState === "error") ? "" : <br/>}
                                    {entry.overlapValidationState === "error" ? "Time overlaps with another entry! " : ""}
                                    {entry.startEndValidationState === "error" ? "End time must be greater than start time! " : ""}
                                    {entry.breakValidationState === "error"  && "Break cannot be longer than work time or incorrect format!"}
                                </div>
                                <div className="entry-editing">
                                    <div className="entry-editing-1">
                                        <TimePicker
                                        key={"entry-start"}
                                        label={"Start"}
                                        value={entry.start}
                                        setValue={handleSetEntry}
                                        validationState={entry.overlapValidationState === "error" ? "error" : "none"}
                                        required={true}
                                        suggestedStart={props.suggestedStartDict[format(new Date(props.entryDay.date), "dd/MM/yyyy")]}
                                        />
                                        <TimePicker
                                        key={"entry-end"}
                                        label={"End"}
                                        value={entry.end}
                                        setValue={handleSetEntry}
                                        validationState={entry.overlapValidationState === "error" ? "error" : entry.startEndValidationState}
                                        required={true}
                                        />
                                        
                                        <TimePicker
                                        key={"entry-break"}
                                        label={"Break"}
                                        value={entry.break}
                                        setValue={handleSetEntry}
                                        required={false}
                                        validationState={entry.breakValidationState}
                                        />
                                    </div>
                                        <div className="editor-total-hours">
                                            Total Hours
                                            <TooltipWatcherWidget text="Total hours for the entry that you are adding/editing" direction="above-start">
                                            <div>{(Math.round((entry.totalMinutes / 60) * 100) / 100).toFixed(2)}hr</div>
                                            </TooltipWatcherWidget>
                                        </div>
                                </div>
                                    
                                Comments<Textarea style={{ width: "100%" }} value={entry.comments} onChange={handleSetEntry} />
                            </div>
                        </div>
                    }                    
                </DialogContent>
                <DialogActions>
                    <DialogTrigger disableButtonEnhancement>
                        <Button 
                            appearance="secondary" 
                            onClick={handleDialogCancel}
                            >Cancel</Button>                    
                    </DialogTrigger>                        
                    <Button 
                        disabled={
                            (!props.dialogOpen) || !entry.isValid || disableConfirmForAPI
                        }
                        icon={loading? <Spinner size="tiny" /> : null}
                        className={globalStyles.buttonPrimary}
                        onClick={handleDialogAdd}
                    >Confirm</Button>
                </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>          
    )
}